import { BaseService } from './base/BaseService'
import type { DocumentTag } from '@/lib/types'
import { ragApi } from '@/services/ApiService'

// Define Tag interface locally since it's not exported from types
export interface Tag {
  id: string
  name: string
  color?: string
  tag_type?: string
  description?: string
}

export interface DocumentTagAssignment {
  tag_id: string
  tag_name: string
  tag_type?: string
}

export interface BulkTagOperation {
  document_ids: number[]
  tag_assignments: DocumentTagAssignment[]
  operation: 'add' | 'remove'
}

export interface DocumentTagsResponse {
  document_id: number
  document_title: string
  tags: DocumentTag[]
  tag_count: number
}

export interface DocumentsByTagsResponse {
  documents: unknown[]
  count: number
  filters: {
    tag_ids: string[]
    match_all: boolean
  }
}

/**
 * Service for managing document tags through RAG API
 */
export class DocumentTagService extends BaseService {
  constructor() {
    super('DocumentTagService')
  }

  /**
   * Get all tags assigned to a specific document
   */
  async getDocumentTags(documentId: string): Promise<DocumentTagsResponse> {
    this.validateRequired({ documentId }, ['documentId'])

    return this.safeOperation('getDocumentTags', async () => {
      const response = await ragApi.get(`/api/v1/rag/documents/${documentId}/tags/`)
      this.log('Document tags retrieved', { documentId, tagCount: (response as { tag_count: number }).tag_count })
      return response as DocumentTagsResponse
    })
  }

  /**
   * Assign tags to a document
   */
  async assignTagsToDocument(
    documentId: string, 
    tagAssignments: DocumentTagAssignment[]
  ): Promise<unknown> {
    this.validateRequired({ documentId, tagAssignments }, ['documentId', 'tagAssignments'])

    return this.safeOperation('assignTagsToDocument', async () => {
      const response = await ragApi.post(`/api/v1/rag/documents/${documentId}/tags/`, {
        tag_assignments: tagAssignments
      })
      this.log('Tags assigned to document', { 
        documentId, 
        assignedCount: (response as { assigned_tags?: unknown[] }).assigned_tags?.length || 0 
      })
      return response
    })
  }

  /**
   * Remove tags from a document
   */
  async removeTagsFromDocument(
    documentId: string, 
    tagIds: string[]
  ): Promise<unknown> {
    this.validateRequired({ documentId, tagIds }, ['documentId', 'tagIds'])

    return this.safeOperation('removeTagsFromDocument', async () => {
      const response = await ragApi.delete(`/api/v1/rag/documents/${documentId}/tags/`, {
        data: { tag_ids: tagIds }
      })
      this.log('Tags removed from document', { 
        documentId, 
        removedCount: (response as { removed_tags?: unknown[] }).removed_tags?.length || 0 
      })
      return response
    })
  }

  /**
   * Get all documents that have specific tags
   */
  async getDocumentsByTags(
    tagIds: string[], 
    matchAll: boolean = false
  ): Promise<DocumentsByTagsResponse> {
    this.validateRequired({ tagIds }, ['tagIds'])

    return this.safeOperation('getDocumentsByTags', async () => {
      const params = {
        tag_ids: tagIds.join(','),
        match_all: matchAll
      }
      const response = await ragApi.get('/api/v1/rag/documents/by-tags/', { params })
      this.log('Documents retrieved by tags', { 
        tagIds, 
        matchAll, 
        documentCount: (response as { count: number }).count 
      })
      return response as DocumentsByTagsResponse
    })
  }

  /**
   * Bulk tag operations on multiple documents
   */
  async bulkTagOperation(operation: BulkTagOperation): Promise<unknown> {
    this.validateRequired(operation as unknown as Record<string, unknown>, ['document_ids', 'tag_assignments', 'operation'])

    return this.safeOperation('bulkTagOperation', async () => {
      const response = await ragApi.post('/api/v1/rag/documents/tags/bulk/', operation)
      this.log('Bulk tag operation completed', { 
        operation: operation.operation,
        documentCount: operation.document_ids.length,
        tagCount: operation.tag_assignments.length
      })
      return response
    })
  }

  /**
   * List all document tags with optional filtering
   */
  async listDocumentTags(filters?: {
    document_id?: number
    tag_id?: string
    tag_type?: string
  }): Promise<DocumentTag[]> {
    return this.safeOperation('listDocumentTags', async () => {
      const params: Record<string, unknown> = {}
      if (filters?.document_id) params.document_id = filters.document_id
      if (filters?.tag_id) params.tag_id = filters.tag_id
      if (filters?.tag_type) params.tag_type = filters.tag_type

      const response = await ragApi.get('/api/v1/rag/documents/tags/', { params })
      this.log('Document tags listed', { filters, count: (response as unknown[]).length || 0 })
      return response as DocumentTag[]
    })
  }

  /**
   * Helper method to convert Tag to DocumentTagAssignment
   */
  tagToAssignment(tag: Tag): DocumentTagAssignment {
    return {
      tag_id: tag.id,
      tag_name: tag.name,
      tag_type: tag.tag_type || 'document'
    }
  }

  /**
   * Helper method to batch assign multiple tags to a document
   */
  async assignMultipleTagsToDocument(
    documentId: string, 
    tags: Tag[]
  ): Promise<unknown> {
    const assignments = tags.map(tag => this.tagToAssignment(tag))
    return this.assignTagsToDocument(documentId, assignments)
  }
}

// Export singleton instance
export const documentTagService = new DocumentTagService()